import React from 'react';
import { Shirt, AlertCircle } from 'lucide-react';
import type { TryOnJob, TryOnGarmentRef } from '../../utils/tryon';

interface TryOnCardProps {
  job: TryOnJob;
  brandColor: string;
  isDark: boolean;
}

/**
 * Virtual Try-On result card (Phase 3). Poll-driven, progressive:
 *   - while queued/processing → loading state + the backend `stage` line
 *   - as soon as `still_url` is non-null → show the try-on still image
 *   - when `video_url` arrives (completed) → show the spin video (autoplay/
 *     loop/muted/playsinline, the HELP_CENTER_MEDIA <video> polish)
 *   - on `error` → friendly retry message (no credit was charged)
 *
 * Mirrors ProductCard/OrderCard visual weight (brand left-border, rounded,
 * dark/light aware). Responsive at 375px.
 */
const TryOnCard: React.FC<TryOnCardProps> = ({ job, brandColor, isDark }) => {
  const cardBg = isDark ? '#1f2937' : '#ffffff';
  const borderColor = isDark ? '#374151' : '#e5e7eb';
  const titleColor = isDark ? '#ffffff' : '#111827';
  const subColor = isDark ? '#9ca3af' : '#6b7280';
  const mediaBg = isDark ? '#111827' : '#f3f4f6';

  // Source garment(s) the try-on is based on — single uses garment_ref, look
  // uses look_ref.products.
  const refs: TryOnGarmentRef[] =
    job.kind === 'look'
      ? (job.look_ref?.products || []).filter(Boolean)
      : job.garment_ref
        ? [job.garment_ref]
        : [];

  const isError = job.status === 'error';
  const hasVideo = !!job.video_url;
  const hasStill = !!job.still_url;
  const isPending = !isError && !hasVideo && !hasStill;
  // Still is up but the spin video is still rendering — the key "actively
  // bringing it to life" moment we want to animate over the image.
  const isRendering = !isError && hasStill && !hasVideo;
  // Any state that needs the loading keyframes injected.
  const isAnimating = isPending || isRendering;

  // Three staggered dots that read as "still working" — inherits the parent
  // text color via currentColor so it works in both dark/light.
  const AnimatedDots: React.FC = () => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px', marginLeft: '2px' }}>
      {[0, 1, 2].map(i => (
        <span
          key={i}
          style={{
            width: '3px',
            height: '3px',
            borderRadius: '50%',
            background: 'currentColor',
            display: 'inline-block',
            animation: `prodagi-tryon-dot 1s ${i * 0.16}s ease-in-out infinite`,
          }}
        />
      ))}
    </span>
  );

  return (
    <div
      style={{
        background: cardBg,
        border: `1px solid ${borderColor}`,
        borderLeft: `4px solid ${brandColor}`,
        borderRadius: '12px',
        overflow: 'hidden',
        margin: '8px 0',
        padding: '12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        maxWidth: '280px',
        boxShadow: isDark ? 'none' : '0 1px 2px rgba(0,0,0,0.04)',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <Shirt size={15} color={brandColor} style={{ flexShrink: 0 }} />
        <div style={{ fontSize: '14px', fontWeight: 700, color: titleColor, lineHeight: '1.3' }}>
          Virtual Try-On
        </div>
      </div>

      {/* Media / state */}
      {isError ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '8px',
            fontSize: '12.5px',
            color: subColor,
            lineHeight: '1.45',
          }}
        >
          <AlertCircle size={16} style={{ flexShrink: 0, marginTop: '1px', color: isDark ? '#f87171' : '#dc2626' }} />
          <span>
            Couldn&apos;t generate your try-on — please try again. No credit was charged.
          </span>
        </div>
      ) : (
        <div
          style={{
            width: '100%',
            borderRadius: '10px',
            overflow: 'hidden',
            background: mediaBg,
            position: 'relative',
            aspectRatio: '3 / 4',
          }}
        >
          {hasVideo ? (
            <video
              src={job.video_url as string}
              poster={job.still_url || undefined}
              autoPlay
              loop
              muted
              playsInline
              controls
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : hasStill ? (
            <>
              <img
                src={job.still_url as string}
                alt="Virtual try-on result"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
              {/* Still is up but the spin video is still generating — sweep a
                  brand-tinted shimmer across the image + a "bringing it to
                  life" badge so it reads as actively working, not frozen. */}
              {isRendering && (
                <>
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      pointerEvents: 'none',
                      background: `linear-gradient(105deg, transparent 35%, ${brandColor}33 50%, transparent 65%)`,
                      backgroundSize: '220% 100%',
                      animation: 'prodagi-tryon-shimmer 1.7s linear infinite',
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      left: '8px',
                      right: '8px',
                      bottom: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '5px 9px',
                      borderRadius: '8px',
                      background: isDark ? 'rgba(17,24,39,0.72)' : 'rgba(255,255,255,0.85)',
                      backdropFilter: 'blur(2px)',
                      WebkitBackdropFilter: 'blur(2px)',
                    }}
                  >
                    <span
                      style={{
                        width: '7px',
                        height: '7px',
                        borderRadius: '50%',
                        background: brandColor,
                        flexShrink: 0,
                        animation: 'prodagi-tryon-pulse 1.2s ease-in-out infinite',
                      }}
                    />
                    <span style={{ fontSize: '11.5px', fontWeight: 600, color: titleColor, lineHeight: '1.3', display: 'inline-flex', alignItems: 'center' }}>
                      {job.stage || 'Bringing it to life…'}
                      <AnimatedDots />
                    </span>
                  </div>
                </>
              )}
            </>
          ) : (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                padding: '12px',
              }}
            >
              <span
                style={{
                  width: '24px',
                  height: '24px',
                  border: `2px solid ${isDark ? '#374151' : '#d1d5db'}`,
                  borderTopColor: brandColor,
                  borderRadius: '50%',
                  display: 'inline-block',
                  animation: 'prodagi-tryon-spin 0.8s linear infinite',
                }}
              />
              <span style={{ fontSize: '12px', color: subColor, textAlign: 'center', lineHeight: '1.4', display: 'inline-flex', alignItems: 'center' }}>
                {job.stage || 'Creating your try-on…'}
                <AnimatedDots />
              </span>
            </div>
          )}
        </div>
      )}

      {/* Source garment / look reference */}
      {refs.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: subColor }}>
            {job.kind === 'look' ? 'Your look' : 'Trying on'}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {refs.slice(0, 4).map((r, i) => (
              <div key={`${r.product_id || i}`} style={{ display: 'flex', alignItems: 'center', gap: '6px', maxWidth: '100%', minWidth: 0 }}>
                {r.image && (
                  <img
                    src={r.image}
                    alt={r.title || ''}
                    style={{ width: '32px', height: '32px', borderRadius: '6px', objectFit: 'cover', flexShrink: 0, border: `1px solid ${borderColor}` }}
                  />
                )}
                {r.title && (
                  <span
                    style={{
                      fontSize: '11.5px',
                      color: titleColor,
                      lineHeight: '1.3',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      minWidth: 0,
                    }}
                  >
                    {r.title}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Local keyframes for the loading treatments (scoped, no global CSS).
          Present while pending (spinner + dots) or rendering (still→video
          shimmer, pulse, dots); gone once the video/error state settles. */}
      {isAnimating && (
        <style>{`
          @keyframes prodagi-tryon-spin { to { transform: rotate(360deg); } }
          @keyframes prodagi-tryon-shimmer { 0% { background-position: 220% 0; } 100% { background-position: -220% 0; } }
          @keyframes prodagi-tryon-pulse { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.5); opacity: 0.45; } }
          @keyframes prodagi-tryon-dot { 0%, 80%, 100% { opacity: 0.25; } 40% { opacity: 1; } }
        `}</style>
      )}
    </div>
  );
};

export default TryOnCard;
